import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import useSpeakerProfiles from '../hooks/useSpeakerProfiles';
import { fromDatetimeLocalValue, toDatetimeLocalValue } from '../dateUtils';
import type { SpeakingEngagement, SpeakingEngagementInput } from '../types';

interface FormState {
  speaker_id: string;
  title: string;
  theme: string;
  start_at: string;
  target_duration_minutes: string;
}

const EMPTY_FORM: FormState = { speaker_id: '', title: '', theme: '', start_at: '', target_duration_minutes: '' };

function toFormState(engagement: SpeakingEngagement | null): FormState {
  if (!engagement) return EMPTY_FORM;
  return {
    speaker_id: engagement.speaker_id,
    title: engagement.title || '',
    theme: engagement.theme || '',
    start_at: toDatetimeLocalValue(engagement.start_at),
    target_duration_minutes: engagement.target_duration_minutes ? String(engagement.target_duration_minutes) : '',
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  engagement: SpeakingEngagement | null;
  onSubmit: (data: SpeakingEngagementInput) => Promise<void>;
}

export default function EngagementFormDialog({ open, onOpenChange, engagement, onSubmit }: Props) {
  const profiles = useSpeakerProfiles();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(toFormState(engagement));
  }, [open, engagement]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.speaker_id || !form.start_at) return;
    setSubmitting(true);
    try {
      await onSubmit({
        speaker_id: form.speaker_id,
        title: form.title || null,
        theme: form.theme || null,
        start_at: fromDatetimeLocalValue(form.start_at),
        target_duration_minutes: form.target_duration_minutes ? Number(form.target_duration_minutes) : null,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{engagement ? 'Spreekbeurt bewerken' : 'Nieuwe spreekbeurt'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="engagement-speaker">Spreker</Label>
            <Select value={form.speaker_id || undefined} onValueChange={(value: string) => setForm({ ...form, speaker_id: value })}>
              <SelectTrigger id="engagement-speaker"><SelectValue placeholder="Kies een spreker" /></SelectTrigger>
              <SelectContent>
                {profiles.map((profile) => (
                  <SelectItem key={profile.id} value={profile.id}>{profile.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {profiles.length === 0 && (
              <p className="mt-1 text-xs text-slate-400">Nog geen sprekersprofielen. Maak er eerst een aan bij Sprekersprofielen.</p>
            )}
          </div>

          <div>
            <Label htmlFor="engagement-title">Titel</Label>
            <Input
              id="engagement-title"
              value={form.title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, title: e.target.value })}
              placeholder="Werktitel of onderwerp"
            />
          </div>

          <div>
            <Label htmlFor="engagement-theme">Thema</Label>
            <Input
              id="engagement-theme"
              value={form.theme}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, theme: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="engagement-start">Datum/tijd</Label>
              <Input
                id="engagement-start"
                type="datetime-local"
                value={form.start_at}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, start_at: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="engagement-duration">Spreektijd (min)</Label>
              <Input
                id="engagement-duration"
                type="number"
                min={0}
                value={form.target_duration_minutes}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, target_duration_minutes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
            <Button type="submit" disabled={submitting || !form.speaker_id || !form.start_at}>
              {engagement ? 'Opslaan' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
